import * as atlas from 'azure-maps-control';
import React, { useContext } from 'react';
import '../../../style/Map.scss';
import { ProductMasterData } from '../../../types/product_master_data/productMasterData';
import StockLocationMap from './StockLocationMap';
import { StockLocation } from '../../../types/stock_location';
import { MobileStockLocationSession, MobileStockLocationSessionStatus } from '../../../types/mobile_stock_location';
import MapPopup from './popups/MapPopup';
import { UserContext } from '../../../context/UserContext';
import { ClusterLayer, ClusterProperties, MarkerLayer, StockLocationMarker } from '.';
import MobileStockLocationSessionProvider from '../../../providers/MobileStockLocationSessionProvider';
import { UnitOfMeasureUtil } from '../../../types/uom';

interface ProductMapProps {
  locations: {
    id: string;
    quantity: number;
  }[];
  productMasterData: ProductMasterData;
}

class StockLocationQuantityMarker extends StockLocationMarker {
  constructor(
    stockLocation: StockLocation,
    public readonly quantity: number,
    public readonly productMasterData: ProductMasterData,
    session?: MobileStockLocationSession,
  ) {
    super(stockLocation, session);
  }

  get properties() {
    return {
      ...super.properties,
      quantity: this.quantity,
      image: this.session?.status === MobileStockLocationSessionStatus.active ? 'marker-green' : 'marker-blue',
    };
  }
}

class StockLocationQuantityMapClusterLayer extends ClusterLayer<StockLocationQuantityMarker> {
  get options(): atlas.SymbolLayerOptions {
    const s = super.options;
    return {
      ...s,
      textOptions: {
        ...s.textOptions,
        textField: ['get', 'quantity'],
      },
    };
  }

  get clusterProperties() {
    return {
      ...super.clusterProperties,
      quantity: ['+', ['get', 'quantity']],
    };
  }

  popupBuilder = (props: ClusterProperties, markers: StockLocationQuantityMarker[]) => {
    const totalQuantity = markers.reduce((sum, marker) => sum + marker.quantity, 0);
    return (
      <MapPopup>
        <p className=' font-medium'>
          {totalQuantity} across {markers.length} locations
        </p>
        <ul>
          {markers.map(marker => (
            <li key={marker.stockLocation.id}>
              {marker.stockLocation.name}: {marker.quantity}
              {UnitOfMeasureUtil.getSuffix(marker.productMasterData.unitOfMeasure)}
            </li>
          ))}
        </ul>
      </MapPopup>
    );
  };
}

export default function ProductMap({ locations, productMasterData }: ProductMapProps) {
  const { companyUsers } = useContext(UserContext);

  class StockLocationQuantityMapMarkerLayer extends MarkerLayer<StockLocationQuantityMarker> {
    get options(): atlas.SymbolLayerOptions {
      const s = super.options;
      return {
        ...s,
        textOptions: {
          ...s.textOptions,
          textField: ['case', ['>', ['get', 'quantity'], 99], `99+`, ['concat', ['get', 'quantity']]],
        },
      };
    }

    markerBuilder = (
      stockLocation: StockLocation,
      session?: MobileStockLocationSession,
    ): StockLocationQuantityMarker => {
      return new StockLocationQuantityMarker(
        stockLocation,
        locations.find(l => l.id === stockLocation.id)!.quantity,
        productMasterData,
        session,
      );
    };

    popupBuilder = (marker: StockLocationQuantityMarker) => {
      const user =
        marker.session &&
        marker.session.status === MobileStockLocationSessionStatus.active &&
        companyUsers.get(marker.session.updatedBy);
      return (
        <MapPopup>
          <p className='font-medium'>{marker.stockLocation.name}</p>
          <p className=''>
            Quantity: {marker.quantity} {UnitOfMeasureUtil.getSuffix(productMasterData.unitOfMeasure)}
          </p>
          {user && (
            <p className=''>
              Driver: {user.firstName} {user.lastName}
            </p>
          )}
        </MapPopup>
      );
    };
  }

  const stockLocationIds = new Set(locations.map(l => l.id));

  return (
    <MobileStockLocationSessionProvider stockLocationIds={stockLocationIds}>
      <StockLocationMap<StockLocationQuantityMarker>
        stockLocationIds={stockLocationIds}
        markerLayer={new StockLocationQuantityMapMarkerLayer()}
        clusterLayer={new StockLocationQuantityMapClusterLayer()}
      />
    </MobileStockLocationSessionProvider>
  );
}
